import { Dispatch } from 'redux';
import { AUTH_LOGIN, AUTH_LOGOUT, LOGIN_ERROR, SEEN_HCP, SEEN_ISI } from '../../../constants/auth.constants';
import { LoginAction, LoginError, LogoutAction, SeenHCP, SeenISI } from '../../../models/redux/actions/auth.action';
import AuthLogin from '../../../models/auth/AuthLogin';
import STORAGE_CONSTANTS from '../../../constants/storage.constants';
import history from '../../../services/browserHistory';
import { success, error, clear } from '../alert/alert.actions';
import { AppDispatch } from '../../../models/redux/dispatch/app.dispatch';
import handleErrors from '../../../utils/handleErrors';
import NETWORK_CONSTANTS from '../../../constants/network.constants';
import authFetch from '../../../utils/authFetch';
import User from '../../../models/user/User';
import AuthRegistration from '../../../models/auth/AuthRegistration';
import AuthPasswordReset from '../../../models/auth/AuthPasswordReset';
import Invite from '../../../models/invite/Invite';
import Profile from '../../../models/profile/Profile';
import { clearProfile, updateProfile } from '../profile/profile.actions';
import { clearTraining } from '../training/training.actions';

export function loginUser(user: User): LoginAction {
  return {
    type: AUTH_LOGIN,
    user,
  };
}

export function logoutUser(): LogoutAction {
  return {
    type: AUTH_LOGOUT,
  };
}

export function setSeenHCP(hasSeen: boolean): SeenHCP {
  return {
    type: SEEN_HCP,
    hasSeen,
  };
}

export function setSeenISI(hasSeen: boolean): SeenISI {
  return {
    type: SEEN_ISI,
    hasSeen,
  };
}

export function setLoginError(errorMessage: string | null): LoginError {
  return {
    type: LOGIN_ERROR,
    errorMessage,
  };
}

const setSeenHCPLocal = (hasSeen: boolean) => {
  return (dispatch: AppDispatch) => {
    if (hasSeen) {
      localStorage.setItem(STORAGE_CONSTANTS.HCP_KEY, STORAGE_CONSTANTS.HCP_KEY);
    } else {
      localStorage.removeItem(STORAGE_CONSTANTS.HCP_KEY);
    }
    dispatch(setSeenHCP(hasSeen));
  };
};

const setSeenISILocal = (hasSeen: boolean) => {
  return (dispatch: AppDispatch) => {
    if (hasSeen) {
      localStorage.setItem(STORAGE_CONSTANTS.ISI_KEY, STORAGE_CONSTANTS.ISI_KEY);
    } else {
      localStorage.removeItem(STORAGE_CONSTANTS.ISI_KEY);
    }
    dispatch(setSeenISI(hasSeen));
  };
};

const registerAPIcall = (token: string, registration: AuthRegistration) => {
  return (dispatch: AppDispatch) => {
    const url = NETWORK_CONSTANTS.BASE_URL + NETWORK_CONSTANTS.API_REGISTER;

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `JWT ${token}`,
      },
      body: JSON.stringify(registration),
    })
      .then(handleErrors)
      .then(() => {
        history.push('../login');
        dispatch(success(`Registered successfully!`));
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
      })
      .catch((err) => {
        dispatch(error(err.message));
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
      });
  };
};

export const selfRegisterAPIcall = (registration: AuthRegistration, callback: (error: string | null) => void) => {
  return async (dispatch: AppDispatch) => {
    const registerUrl = `${NETWORK_CONSTANTS.BASE_URL}${NETWORK_CONSTANTS.API_SELF_REGISTER}`;
    try {
      const registerResponse = await fetch(registerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registration),
      });

      if (!registerResponse.ok) {
        const errorData = await registerResponse.json();
        const errorMessage =
          errorData?.email?.[0] ||
          errorData?.username?.[0] ||
          errorData?.detail ||
          'An error occurred during registration.';
        throw new Error(errorMessage);
      }

      const loginCredentials = { email: registration.email, password: registration.password };

      dispatch(
        loginAPIcall(loginCredentials, (error) => {
          if (error) {
            console.error('Login failed after registration:', error);
            callback(error); // Notify the caller of the login failure
          } else {
            callback(null); // Notify the caller of success
          }
        }),
      );
    } catch (err) {
      console.error('Self-registration error:', err.message || 'An unknown error occurred');
      callback(err.message || 'An unknown error occurred');
    }
  };
};

const registerWithPictureAPIcall = (token: string, registration: AuthRegistration, profilePicture: File) => {
  return (dispatch: AppDispatch) => {
    const url = NETWORK_CONSTANTS.BASE_URL + NETWORK_CONSTANTS.API_REGISTER;

    const formData = new FormData();
    formData.append('first_name', registration.firstName);
    formData.append('last_name', registration.lastName);
    formData.append('email', registration.email);
    formData.append('password', registration.password);
    formData.append('username', registration.username);
    formData.append('profile_picture', profilePicture);

    return fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `JWT ${token}`,
      },
      body: formData,
    })
      .then(handleErrors)
      .then(() => {
        history.push('../login');
        dispatch(success(`Registered ${registration.email} successfully!`));
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
      })
      .catch((err) => {
        dispatch(error(err.message));
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
      });
  };
};

const patchProfileAPIcall = (url: string, formValues: Profile) => {
  return (dispatch: AppDispatch) => {
    return authFetch(dispatch, url, 'PATCH', JSON.stringify(formValues))
      .then((json) => {
        dispatch(updateProfile(json));
        dispatch(success('Success!'));
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
        return json;
      })
      .catch((err) => {
        dispatch(error(err.message));
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
        throw err;
      });
  };
};

const patchCurrentUserAPIcall = (url: string, formValues: Partial<User>) => {
  return (dispatch: AppDispatch) => {
    return authFetch(dispatch, url, 'PATCH', JSON.stringify(formValues))
      .then((json) => {
        dispatch(loginUser(json));
        dispatch(success('Success!'));
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
        return json;
      })
      .catch((err) => {
        dispatch(error(err.message));
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
        throw err;
      });
  };
};

const getCurrentUserAPIcall = (callback?: (user: User) => void) => {
  return (dispatch: AppDispatch) => {
    const url = `${NETWORK_CONSTANTS.BASE_URL}${NETWORK_CONSTANTS.API_USERS}?current=true`;

    return authFetch(dispatch, url, 'GET')
      .then((json) => {
        dispatch(loginUser(json.results[0]));
        if (callback) {
          callback(json.results[0]);
        }
        return json.results[0];
      })
      .catch((err) => {
        dispatch(error(err.message));
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
        throw err;
      });
  };
};

const getUserByUrlAPIcall = (url: string, callback?: (user: User) => void) => {
  return (dispatch: AppDispatch) => {
    return authFetch(dispatch, url, 'GET')
      .then((json) => {
        dispatch(loginUser(json));
        if (callback) {
          callback(json);
        }
        return json;
      })
      .catch((err) => {
        dispatch(error(err.message));
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
        throw err;
      });
  };
};

const loginAPIcall = (login: AuthLogin) => {
  return (dispatch: Dispatch<any>) => {
    const url = NETWORK_CONSTANTS.BASE_URL + NETWORK_CONSTANTS.API_LOGIN;

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: login.email, password: login.password }),
    })
      .then(handleErrors)
      .then(async (json) => {
        localStorage.setItem(STORAGE_CONSTANTS.TOKEN_KEY, json.token);

        dispatch(logoutUser());
        dispatch(clearProfile());
        dispatch(clearTraining());
        dispatch(updateProfile(json.profile));

        const userUrl = `${NETWORK_CONSTANTS.BASE_URL}${NETWORK_CONSTANTS.API_USERS}${json.token ? JSON.parse(atob(json.token.split('.')[1])).user_id : ''}/`;

        await dispatch(getUserByUrlAPIcall(userUrl));
        history.push('/trainings');
      })
      .catch((err) => {
        dispatch(setLoginError(err.toString()));
        localStorage.removeItem(STORAGE_CONSTANTS.TOKEN_KEY);
      });
  };
};

const logoutAPIcall = (showSuccess = true) => {
  // use any instead of AppDispatch so we can dispatch other async actions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (dispatch: any) => {
    // TODO: Notify server that user has logged out?
    localStorage.removeItem(STORAGE_CONSTANTS.TOKEN_KEY);
    dispatch(setSeenHCPLocal(false));
    dispatch(setSeenISILocal(false));
    dispatch(clearProfile());
    dispatch(clearTraining());
    dispatch(logoutUser());
    history.push('/login');
    if (showSuccess) {
      dispatch(success('Logged out successfully'));
      setTimeout(() => {
        dispatch(clear());
      }, 6000);
    }
  };
};

const resetPasswordAPIcall = (email: AuthPasswordReset) => {
  return (dispatch: AppDispatch) => {
    const url = NETWORK_CONSTANTS.BASE_URL + NETWORK_CONSTANTS.API_FORGOT_PASSWORD;

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(email),
    })
      .then(handleErrors)
      .then(() => {
        history.push('../login');
        dispatch(success('Success! Please check email for instructions.'));
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
      })
      .catch((err) => {
        dispatch(error(err.message));
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
      });
  };
};

const setPasswordAPIcall = (password: string, token?: string) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (dispatch: any) => {
    const url = NETWORK_CONSTANTS.BASE_URL + NETWORK_CONSTANTS.API_SET_PASSWORD;
    if (token) {
      fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `JWT ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      })
        .then(handleErrors)
        .then(() => {
          dispatch(logoutAPIcall());
          dispatch(success('Success! Please login with new credentials.'));
          setTimeout(() => {
            dispatch(clear());
          }, 6000);
        })
        .catch((err) => {
          dispatch(error(err.message));
          setTimeout(() => {
            dispatch(clear());
          }, 6000);
        });
    } else {
      authFetch(dispatch, url, 'POST', JSON.stringify({ password }))
        .then(handleErrors)
        .then(() => {
          dispatch(logoutAPIcall());
          dispatch(success('Success! Please login with new credentials.'));
          setTimeout(() => {
            dispatch(clear());
          }, 6000);
        })
        .catch((err) => {
          dispatch(error(err.message));
          setTimeout(() => {
            dispatch(clear());
          }, 6000);
        });
    }
  };
};

const inviteUserAPIcall = (invite: Invite, resetForm: () => void) => {
  return (dispatch: AppDispatch) => {
    const url = NETWORK_CONSTANTS.BASE_URL + NETWORK_CONSTANTS.API_INVITE_USER;

    authFetch(dispatch, url, 'POST', JSON.stringify(invite))
      .then(() => {
        dispatch(success(`Success! Invite sent to ${invite.emailAddress}`));
        resetForm();
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
      })
      .catch((err) => {
        dispatch(error(err.message));
        setTimeout(() => {
          dispatch(clear());
        }, 6000);
      });
  };
};

export {
  registerAPIcall,
  registerWithPictureAPIcall,
  loginAPIcall,
  logoutAPIcall,
  resetPasswordAPIcall,
  setPasswordAPIcall,
  getCurrentUserAPIcall,
  patchCurrentUserAPIcall,
  patchProfileAPIcall,
  inviteUserAPIcall,
  setSeenHCPLocal,
  setSeenISILocal,
  getUserByUrlAPIcall,
};
